/**
 * `useDueReminders` (F2 / decision D5) — loads the server-backed calendar
 * and dosing-schedule namespaces, computes what is due *today* (plus any
 * overdue one-off events whose date has already passed), and fires a
 * browser pop-up notification per due/overdue item (de-duped per day via
 * `useBrowserNotifications`). The returned counts/lists drive the in-app
 * badge + alerts dropdown so both D5 channels stay in sync.
 *
 * In-app surfaces are always populated; browser pop-ups only fire when
 * the operator has granted notification permission. When permission is
 * absent the badge/list remain the (graceful) primary channel.
 */
import { useEffect, useMemo } from 'react';

import {
  EMPTY_CALENDAR_DATA,
  EMPTY_DOSING_SCHEDULE_DATA,
  isDueOn,
  recurrenceDueOn,
  type CalendarData,
  type CalendarEvent,
  type DosingScheduleData,
  type DosingScheduleEntry,
} from '@reefapp/shared';

import { useServerBackedState } from '../hooks/useServerBackedState';
import { useBrowserNotifications } from '../notifications/useBrowserNotifications';
import { compareIso, localTodayIso } from './localDate';

export interface DueReminders {
  /** Recurring/one-off events due on `today`. */
  readonly dueEvents: readonly CalendarEvent[];
  /** One-off events whose date is strictly before `today` (never cleared). */
  readonly overdueEvents: readonly CalendarEvent[];
  /** Dosing entries due on `today`. */
  readonly dueDosing: readonly DosingScheduleEntry[];
  /** Total badge count = due events + overdue events + due dosing. */
  readonly count: number;
  readonly loading: boolean;
}

/**
 * A one-off event is "overdue" when its single date already passed and it
 * was therefore never matched by `isDueOn(today)`. Recurring events have
 * no overdue concept (they simply fire on their next occurrence).
 */
function isOverdueOnceEvent(event: CalendarEvent, todayIso: string): boolean {
  return (
    event.recurrence.kind === 'once' &&
    compareIso(event.recurrence.date, todayIso) < 0
  );
}

export function useDueReminders(now: Date = new Date()): DueReminders {
  const todayIso = localTodayIso(now);

  const { value: calendar, loading: calLoading } =
    useServerBackedState<CalendarData>('calendar', EMPTY_CALENDAR_DATA);
  const { value: dosing, loading: dosingLoading } =
    useServerBackedState<DosingScheduleData>(
      'dosing-schedule',
      EMPTY_DOSING_SCHEDULE_DATA,
    );

  const { notify } = useBrowserNotifications();

  const dueEvents = useMemo(
    () => calendar.events.filter((e) => isDueOn(e, todayIso)),
    [calendar.events, todayIso],
  );

  const overdueEvents = useMemo(
    () => calendar.events.filter((e) => isOverdueOnceEvent(e, todayIso)),
    [calendar.events, todayIso],
  );

  const dueDosing = useMemo(
    () =>
      dosing.entries.filter((entry) =>
        recurrenceDueOn(entry.recurrence, todayIso),
      ),
    [dosing.entries, todayIso],
  );

  const count = dueEvents.length + overdueEvents.length + dueDosing.length;

  // Fire browser pop-ups for everything due/overdue today. The hook
  // de-dupes by key per calendar day, so re-renders never re-fire the
  // same reminder. Keys are namespaced + dated so a recurring event
  // fires again tomorrow.
  useEffect(() => {
    for (const e of dueEvents) {
      notify(
        `event:${e.id}:${todayIso}`,
        e.title,
        e.time ? `Due today at ${e.time}` : 'Due today',
      );
    }
    for (const e of overdueEvents) {
      notify(
        `overdue:${e.id}:${todayIso}`,
        e.title,
        `Overdue (was due ${e.recurrence.kind === 'once' ? e.recurrence.date : ''})`.trim(),
      );
    }
    for (const entry of dueDosing) {
      notify(
        `dosing:${entry.id}:${todayIso}`,
        `Dose ${entry.productName}`,
        `${entry.amountMl} ml${entry.time ? ` at ${entry.time}` : ''}`,
      );
    }
  }, [dueEvents, overdueEvents, dueDosing, notify, todayIso]);

  return {
    dueEvents,
    overdueEvents,
    dueDosing,
    count,
    loading: calLoading || dosingLoading,
  };
}
